import React, { useRef, useLayoutEffect } from 'react';
import DayPicker from 'react-day-picker';
import MomentLocaleUtils from 'react-day-picker/moment';
import { CalendarPerfMonitor } from '../CalendarPerfMonitor';

export interface PharmacyCalendarDatePickerProps {
  initialMonth: Date;
  minDate: Date;
  maxDate: Date;
  modifiers: Record<string, (day: Date) => boolean>;
  onChange: (selectedDate: Date) => void;
}

export const PharmacyCalendarDatePicker = ({
  initialMonth,
  minDate,
  maxDate,
  modifiers,
  onChange,
}: PharmacyCalendarDatePickerProps) => {
  const monthLabel = `${initialMonth.getFullYear()}-${String(initialMonth.getMonth() + 1).padStart(2, '0')}`;
  const renderStartRef = useRef(performance.now());
  renderStartRef.current = performance.now();

  // Fires after DOM commit — captures actual reconciliation + DOM update time for this DayPicker.
  useLayoutEffect(() => {
    CalendarPerfMonitor.accumulate(`DatePicker[${monthLabel}]`, performance.now() - renderStartRef.current);
  });

  // Use react-day-picker's DayPicker directly instead of Blueprint's DatePicker wrapper.
  // The `month` prop here is CONTROLLED (reactive to changes), unlike Blueprint's `initialMonth`
  // which is uncontrolled. This allows parent components to use stable React keys across year
  // navigations — DayPicker updates its view when `month` changes rather than requiring a remount.
  return (
    <div className="bp3-datepicker">
      <DayPicker
        month={initialMonth}
        fromMonth={minDate}
        toMonth={maxDate}
        modifiers={modifiers}
        onDayClick={(day, mods) => {
          if (!mods.disabled && !mods.outside) onChange(day);
        }}
        firstDayOfWeek={1}
        localeUtils={MomentLocaleUtils}
        locale="fr"
        showOutsideDays={false}
        disabledDays={[{ before: minDate }, { after: maxDate }]}
      />
    </div>
  );
};
